import { useQuery } from "@apollo/client";
import { DiscrepancyStatus, WasteManifestSource } from "generated-graphql/graphql";
import { useMemo } from "react";

import { MANIFEST_DISCREPANCIES_QUERY } from "./api";
import { discrepancyFieldToUiKey } from "./manifestFieldNames";

/** A discrepancy is "open" (warrants a validation icon) when unresolved/pending. */
const OPEN_STATUSES = new Set<DiscrepancyStatus>([
  DiscrepancyStatus.Unresolved,
  DiscrepancyStatus.Pending,
]);

/** Per-source values for one field, as reported by a discrepancy row. */
export type FieldSourceValues = Partial<Record<WasteManifestSource, string>>;

export interface ManifestDiscrepancyFields {
  /** Manifest-level field UI keys (no waste line) with an open discrepancy. */
  disagreementFields: Set<string>;
  /** Per-waste-line field UI keys with an open discrepancy, keyed by wasteLineUnifiedId. */
  lineItemDisagreementFields: Map<string, Set<string>>;
  /**
   * Per-source values keyed by UI field key — populated from discrepancy rows
   * (which carry eManifest/vendor/document values). Drives both source-switched
   * header values and the provenance popover. Only fields that actually differ
   * across sources appear here; agreeing fields are read from the fragment.
   */
  valuesByField: Map<string, FieldSourceValues>;
  /**
   * Per-source values for line-item fields, keyed by wasteLineUnifiedId → UI
   * field key. Feeds the line-item modal's "sources disagree" tooltips.
   */
  lineItemValuesByField: Map<string, Map<string, FieldSourceValues>>;
  loading: boolean;
}

/**
 * Loads the manifest's discrepancies and derives, client-side, which rendered
 * fields disagree across sources — the M2 validation indicators (item 5).
 * No new backend field: the warning icon is purely a read of existing rows.
 *
 * Callers may skip the query when the compare experience is unavailable. It
 * also skips when there is no manifest id, so the drawer degrades to no warnings.
 */
export function useManifestDiscrepancyFields(
  processedManifestId: string | undefined,
  options?: { skip?: boolean }
): ManifestDiscrepancyFields {
  const { data, loading } = useQuery(MANIFEST_DISCREPANCIES_QUERY, {
    variables: { processedManifestId: processedManifestId ?? "" },
    skip: options?.skip || !processedManifestId,
    fetchPolicy: "cache-and-network",
  });

  return useMemo(() => {
    const disagreementFields = new Set<string>();
    const lineItemDisagreementFields = new Map<string, Set<string>>();
    const valuesByField = new Map<string, FieldSourceValues>();
    const lineItemValuesByField = new Map<string, Map<string, FieldSourceValues>>();

    for (const row of data?.manifestDiscrepancies ?? []) {
      const uiKey = discrepancyFieldToUiKey(row.field);

      // Record per-source values for every row (open or not) so the popover can
      // always show what each source reported.
      const sourceValues: FieldSourceValues = {};
      if (row.eManifestValue != null)
        sourceValues[WasteManifestSource.EManifest] = row.eManifestValue;
      if (row.vendorValue != null) sourceValues[WasteManifestSource.Vendor] = row.vendorValue;
      if (row.manifestDocumentValue != null) {
        sourceValues[WasteManifestSource.ManifestDocument] = row.manifestDocumentValue;
      }
      const hasSourceValues = Object.keys(sourceValues).length > 0;
      if (hasSourceValues && !row.wasteLineUnifiedId) {
        valuesByField.set(uiKey, { ...valuesByField.get(uiKey), ...sourceValues });
      } else if (hasSourceValues && row.wasteLineUnifiedId) {
        const lineValues =
          lineItemValuesByField.get(row.wasteLineUnifiedId) ?? new Map<string, FieldSourceValues>();
        lineValues.set(uiKey, { ...lineValues.get(uiKey), ...sourceValues });
        lineItemValuesByField.set(row.wasteLineUnifiedId, lineValues);
      }

      if (!OPEN_STATUSES.has(row.status)) continue;
      if (row.wasteLineUnifiedId) {
        const set = lineItemDisagreementFields.get(row.wasteLineUnifiedId) ?? new Set<string>();
        set.add(uiKey);
        lineItemDisagreementFields.set(row.wasteLineUnifiedId, set);
      } else {
        disagreementFields.add(uiKey);
      }
    }

    return {
      disagreementFields,
      lineItemDisagreementFields,
      valuesByField,
      lineItemValuesByField,
      loading,
    };
  }, [data, loading]);
}
